import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertCircle, AlertTriangle, ArrowRight, Check, CheckCircle2, Clock3, ListChecks, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { ActionBar, BarButton } from '@generated/components/form/ActionBar';
import { CancelRequestDialog } from '@generated/components/form/CancelRequestDialog';
import { PostBiddingDocument } from '@generated/components/form/PostBiddingDocument';
import { PostBiddingEditor } from '@generated/components/form/PostBiddingEditor';
import { PostBiddingReceipt } from '@generated/components/form/PostBiddingReceipt';
import { TurnstileWidget } from '@generated/components/TurnstileWidget';
import { usePostBiddingCatalog } from '@generated/hooks/usePostBiddingCatalog';
import { usePostBiddingForm } from '@generated/hooks/usePostBiddingForm';
import { academicYearTitle } from '@generated/lib/academicYear';
import { maxRequests } from '@generated/lib/postbidding/model';
import { createPostBiddingRequest } from '@generated/server/postBidding';
import { checkDuplicateCourseRequests, checkSubmissionQuota } from '@generated/server/duplicateCheck';
import { getPostBiddingFormStatus } from '@generated/server/formSettings';
import { HiddenAdminToggle } from '@generated/components/form/HiddenAdminToggle';
import { PostBiddingClosedNotice } from '@generated/components/form/PostBiddingClosedNotice';

export const Route = createFileRoute('/_app/forms/post-bidding')({
  component: PostBiddingPage,
});

type Mode = 'edit' | 'review' | 'submitted';

function makeReference(): string {
  const now = new Date();
  const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
  const suffix = String(now.getHours()).padStart(2, '0') + String(now.getMinutes()).padStart(2, '0');
  return `PB-${stamp}-${suffix}`;
}

function PostBiddingPage() {
  const api = usePostBiddingForm();
  const catalog = usePostBiddingCatalog();
  const [mode, setMode] = useState<Mode>('edit');
  const [reference, setReference] = useState('');
  const [saved, setSaved] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(true);
  const [formCloseAt, setFormCloseAt] = useState<string | null>(null);
  const [formStatusLoaded, setFormStatusLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    getPostBiddingFormStatus()
      .then((res) => {
        if (!active) return;
        setFormOpen(res?.isOpen ?? true);
        setFormCloseAt(res?.closeAt ?? null);
      })
      .catch((err) => console.error('[POST-BIDDING] Failed to load form status', err))
      .finally(() => {
        if (active) setFormStatusLoaded(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const dirty = Boolean(api.values.idNumber || api.values.firstName || api.values.requests[0]?.courseName);

  useEffect(() => {
    if (mode !== 'edit' || !dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty, mode]);

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      const status = await getPostBiddingFormStatus();
      if (!(status?.isOpen ?? true)) {
        setSubmitting(false);
        setFormOpen(false);
        toast.error('הרישום לטופס נסגר בזמן שמילאת אותו. ניתן לבטל בקשה קיימת בלבד.');
        return;
      }
    } catch (error) {
      console.error('Failed to verify the form is open before submitting', error);
    }

    try {
      const quota = await checkSubmissionQuota({
        data: { idNumber: api.values.idNumber, bidding: api.values.bidding, requestedCount: api.activeCount },
      });
      if (!quota?.checkSucceeded || !quota.allowed) {
        setSubmitting(false);
        setBlockedMessage(quota?.message ?? 'לא ניתן היה לאמת את יתרת ההגשות כרגע. נסו שוב בעוד מספר רגעים.');
        return;
      }

      // Block resubmission of a course the student already has an active request for — unless that
      // prior request was cancelled at the student's own request.
      const requestedCourseNumbers = api.values.requests
        .slice(0, api.activeCount)
        .map((r) => r.courseNumber.trim())
        .filter(Boolean);

      if (requestedCourseNumbers.length > 0) {
        const duplicateCheck = await checkDuplicateCourseRequests({
          data: { idNumber: api.values.idNumber, courseNumbers: requestedCourseNumbers },
        });
        if (!duplicateCheck?.checkSucceeded || !duplicateCheck.allowed) {
          setSubmitting(false);
          setBlockedMessage(
            duplicateCheck?.message ?? 'לא ניתן היה לאמת כפילות בקשות כרגע. נסו שוב בעוד מספר רגעים.',
          );
          return;
        }
      }
    } catch (error) {
      console.error('Failed to check the submission quota', error);
      setSubmitting(false);
      setBlockedMessage('לא ניתן היה לאמת את יתרת ההגשות כרגע. נסו שוב בעוד מספר רגעים.');
      return;
    }

    const requests = api.values.requests.slice(0, api.activeCount);
    const savedCourses: string[] = [];
    const failed: string[] = [];

    // ONE item per request, created one after the other so a single rejected request never takes the
    // others down — and so the receipt can name exactly which ones reached the board.
    for (const request of requests) {
      try {
        await createPostBiddingRequest({
          data: {
            idNumber: api.values.idNumber,
            firstName: api.values.firstName,
            lastName: api.values.lastName,
            email: api.values.email,
            bidding: api.values.bidding,
            program: api.values.program,
            academicYear: api.values.academicYear,
            studyYear: api.values.studyYear,
            semester: api.values.semester,
            courseType: request.courseType,
            courseName: request.courseName,
            courseNumber: request.courseNumber,
            structuredDescription: request.structuredDescription,
            teachingMode: request.teachingMode,
            registrationReason: request.registrationReason,
            notes: (request.notes || api.values.notes || '').slice(0, 100),
            turnstileToken: turnstileToken || undefined,
          },
        });
        savedCourses.push(request.courseName);
      } catch (error) {
        console.error('Failed to create a post-bidding request', error);
        failed.push(request.courseName);
      }
    }

    setSubmitting(false);

    if (savedCourses.length === 0) {
      setSubmitError('הבקשות לא נשמרו בלוח. נסו שוב בעוד מספר רגעים.');
      toast.error('שליחת הבקשות נכשלה');
      return;
    }

    const code = makeReference();
    setReference(code);
    setSaved(savedCourses);
    api.clearDraftStorage();
    setMode('submitted');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (failed.length > 0) {
      setSubmitError(`הבקשות הבאות לא נשמרו: ${failed.join(', ')}. יש להגיש אותן שוב.`);
      toast.warning('חלק מהבקשות לא נשמרו', { description: failed.join(', '), duration: 12000 });
    } else {
      toast.success('הבקשות הוגשו ונשמרו בלוח', { description: `אסמכתא ${code}` });
    }
  };

  const startNew = () => {
    api.reset();
    setReference('');
    setSaved([]);
    setSubmitError(null);
    setMode('edit');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const blockedDialog = (
    <Dialog open={Boolean(blockedMessage)} onOpenChange={(open) => !open && setBlockedMessage(null)}>
      <DialogContent className="border-destructive/40">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--destructive)/0.12)] text-destructive">
              <AlertTriangle className="size-5" aria-hidden="true" />
            </span>
            <div className="min-w-0 text-right">
              <DialogTitle>לא ניתן להגיש את הבקשה</DialogTitle>
              <DialogDescription className="mt-1.5 leading-relaxed">{blockedMessage}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogFooter>
          <BarButton tone="ghost" onClick={() => setBlockedMessage(null)}>
            הבנתי
          </BarButton>
          <BarButton
            tone="primary"
            onClick={() => {
              setBlockedMessage(null);
              setCancelOpen(true);
            }}
          >
            <Undo2 className="size-4" aria-hidden="true" />
            ביטול בקשה קיימת
          </BarButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const cancelDialog = (
    <CancelRequestDialog
      open={cancelOpen}
      onOpenChange={setCancelOpen}
      onCancelled={() => {
        // The card is now "בוטל לבקשת ס��ודנט", which frees a quota slot — the student continues
        // with the submission they already filled in.
        setBlockedMessage(null);
        toast.success('הבקשה הקודמת בוטלה', { description: 'ניתן להמשיך ולהגיש את הבקשה החדשה.' });
      }}
    />
  );

  const handleStatusChange = (isOpen: boolean, closeAt?: string | null) => {
    setFormOpen(isOpen);
    setFormCloseAt(closeAt ?? null);
  };

  if (mode === 'submitted') {
    return (
      <div className="px-4 py-8 sm:px-6 lg:py-12">
        <HiddenAdminToggle onStatusChange={handleStatusChange} />
        {blockedDialog}
        {cancelDialog}
        {submitError ? (
          <div
            role="alert"
            className="mx-auto mb-5 max-w-[900px] border border-destructive bg-[hsl(var(--surface-1))] px-4 py-3 text-sm leading-relaxed text-foreground"
          >
            {submitError}
          </div>
        ) : null}
        <PostBiddingReceipt
          values={api.values}
          activeCount={api.activeCount}
          reference={reference}
          savedCourses={saved}
          onNewForm={startNew}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1180px] px-4 py-8 sm:px-6 lg:py-12">
      <HiddenAdminToggle onStatusChange={handleStatusChange} />
      {blockedDialog}
      {cancelDialog}
      <nav
        aria-label="מסלול ניווט"
        className="no-print mb-6 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground"
      >
        <button
          type="button"
          onClick={() => setCancelOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] px-3 py-1.5 font-medium text-foreground shadow-2xs transition-colors hover:border-primary hover:text-primary active:scale-95"
        >
          <Undo2 className="size-3.5 text-primary" aria-hidden="true" />
          <span>ביטול בקשה קיימת</span>
        </button>

        <Link to="/" className="inline-flex items-center gap-1.5 hover:text-foreground">
          <span>חזרה לטפסים</span>
          <ArrowRight className="size-3.5" aria-hidden="true" />
        </Link>
      </nav>

      <header className="no-print mb-6 border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] px-6 py-10 text-center sm:px-10">
        <p className="text-[11px] font-medium tracking-[0.16em] text-muted-foreground">{academicYearTitle()}</p>
        <h1 className="mt-3 text-2xl font-bold leading-tight text-foreground sm:text-3xl">טופס בקשות רישום ידניות חריגות לאחר בידינג</h1>
        <p className="mt-2 text-sm text-muted-foreground">בקשת רישום לקורסים לאחר שלב הבידינג</p>
      </header>

      {/* Scheduled Closing Notice Banner (if scheduled in the future and form is currently open) */}
      {formOpen && formCloseAt && new Date(formCloseAt).getTime() > Date.now() && (
        <div className="no-print mb-6 flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3.5 text-xs text-amber-900 dark:text-amber-200 shadow-2xs">
          <Clock3 className="size-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <span>
            <strong>שימו לב — מועד סגירת ההרשמה:</strong> הרישום לטופס זה ייסגר אוטומטית בתאריך{' '}
            {new Date(formCloseAt).toLocaleDateString('he-IL')} בשעה{' '}
            {new Date(formCloseAt).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}.
          </span>
        </div>
      )}

      {!formStatusLoaded ? null : !formOpen ? (
        <PostBiddingClosedNotice onOpenCancelDialog={() => setCancelOpen(true)} closeAt={formCloseAt} />
      ) : (
        <>
          {/* Quick cancellation callout banner */}
          <div className="no-print mb-6 flex flex-col items-start justify-between gap-3 rounded-md border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] p-4 shadow-2xs sm:flex-row sm:items-center sm:px-5">
            <div className="flex items-center gap-3">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Undo2 className="size-4" aria-hidden="true" />
              </div>
              <div>
                <p className="text-xs font-semibold text-foreground">מעוניינים לבטל בקשה שהגשתם בעבר?</p>
                <p className="text-[11px] text-muted-foreground">ביטול בקשה קיימת מפנה מיד מקום במכסה ומאפשר להגיש בקשה לקורס חלופי.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCancelOpen(true)}
              className="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground shadow-xs transition-colors hover:bg-primary/90 active:scale-95"
            >
              <Undo2 className="size-3.5" aria-hidden="true" />
              <span>ביטול בקשה</span>
            </button>
          </div>

          <dl className="no-print mb-6 grid gap-px overflow-hidden border border-[hsl(var(--rule))] bg-[hsl(var(--rule-soft))] sm:grid-cols-3">
            {[
              { icon: <Clock3 className="size-4" aria-hidden="true" />, label: 'זמן מילוי משוער', value: 'כ-4 דקות' },
              {
                icon: <ListChecks className="size-4" aria-hidden="true" />,
                label: 'כמות בקשות',
                value: `עד ${maxRequests(api.values.biddingAnswer)} קורסים`,
              },
              { icon: <Check className="size-4" aria-hidden="true" />, label: 'ללא צירוף קבצים', value: 'בחירה מתוך תכנית הלימוד' },
            ].map((item) => (
              <div key={item.label} className="bg-[hsl(var(--surface-1))] px-4 py-3">
                <dt className="flex items-center gap-2 text-[11px] font-medium tracking-[0.1em] text-muted-foreground">
                  {item.icon}
                  {item.label}
                </dt>
                <dd className="mt-1 text-sm text-foreground">{item.value}</dd>
              </div>
            ))}
          </dl>

          {mode === 'edit' ? (
            <PostBiddingEditor
              api={api}
              rows={catalog.rows}
              biddingChoices={catalog.biddingChoices}
              loading={catalog.loading}
              catalogError={catalog.error}
              retrying={catalog.retrying}
              onRetry={catalog.retry}
              notes={catalog.notes}
              onContinue={() => {
                setMode('review');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          ) : (
            <div className="mx-auto max-w-[900px]">
              <div className="no-print mb-5 border border-[hsl(var(--rule))] bg-[hsl(var(--tint-sky))] px-4 py-3 text-sm leading-relaxed text-foreground">
                זהו סיכום הבקשות לפני ההגשה. כל בקשה תיווצר כפנייה נפרדת בלוח מדור התלמידים.
              </div>
              <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))]">
                <PostBiddingDocument values={api.values} activeCount={api.activeCount} />
              </div>

              <div className="mt-5">
                <TurnstileWidget
                  onVerify={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken(null)}
                />
              </div>

              {submitError ? <p className="mt-3 text-sm font-medium text-destructive">{submitError}</p> : null}
              <ActionBar
                status={
                  turnstileToken ? (
                    <span className="flex items-center gap-1.5 font-semibold text-emerald-700 dark:text-emerald-400 text-xs sm:text-sm">
                      <CheckCircle2 className="size-4 shrink-0" />
                      אימות האבטחה הושלם — הטופס מוכן לשליחה.
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 font-medium text-amber-800 dark:text-amber-200 bg-amber-500/10 px-2.5 py-1 rounded border border-amber-500/30 text-xs">
                      <AlertCircle className="size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
                      חובה לסמן את ריבוע האימות ("אני לא רובוט") למעלה כדי לשלוח את הטופס
                    </span>
                  )
                }
              >
                <BarButton tone="ghost" onClick={() => setMode('edit')} disabled={submitting}>
                  חזרה לעריכה
                </BarButton>
                <BarButton
                  tone="primary"
                  onClick={() => {
                    if (!turnstileToken) {
                      const el = document.getElementById('human-verification-widget');
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                      toast.error('חובה לסמן את ריבוע האימות "אני לא רובוט" לפני שליחת הטופס.');
                      return;
                    }
                    submit();
                  }}
                  disabled={submitting}
                  className={!turnstileToken ? 'opacity-60 cursor-not-allowed' : ''}
                >
                  <Check className="size-4" aria-hidden="true" />
                  {submitting ? 'שולח…' : 'שליחת הבקשות'}
                </BarButton>
              </ActionBar>
            </div>
          )}
        </>
      )}
    </div>
  );
}
